import { useCallback, useContext, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Health } from '@capgo/capacitor-health'
import { AuthContext } from '../App'
import { getHealthEvents, putHealthEvents } from '../api/user/profile/Profile'
import { useEffect } from 'react'

const READ_DATA_TYPES = ['calories', 'steps', 'distance'] as const
const SYNC_DEBOUNCE_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Request health data authorization.
 * Works on both iOS (HealthKit) and Android (Health Connect) via @capgo/capacitor-health.
 */
export const requestHealthKitAuthorization = async (): Promise<boolean> => {
  try {
    const available = await Health.isAvailable()
    if (!available.available) {
      console.warn('[Health] Health data not available on this device:', available.reason)
      return false
    }

    const result = await Health.requestAuthorization({
      read: [...READ_DATA_TYPES],
      write: [],
    })
    // If at least some read types were authorized, consider it a success
    return result.readAuthorized.length > 0
  } catch (error) {
    console.error('[Health] Authorization failed:', error)
    return false
  }
}

/**
 * Check if health data is available on this device (iOS or Android native only).
 */
export const isHealthAvailable = (): boolean => {
  return Capacitor.isNativePlatform()
}

export const useHealthKitSync = () => {
  const { user } = useContext(AuthContext)
  const queryClient = useQueryClient()
  const lastSyncRef = useRef<number>(0)

  const isNative = Capacitor.isNativePlatform()

  const isConnected = user?.member_profile?.healthkit_connected === true

  const missingActivityEvents = useQuery(
    ['missingActivityEvents'],
    () => getHealthEvents({ has_healthkit_data: false }),
    {
      enabled: isNative && isConnected,
      refetchOnWindowFocus: false,
    }
  )

  const putActivityData = useMutation(['putActivityData'], putHealthEvents, {
    onSuccess: () => {
      queryClient.invalidateQueries(['auth'])
    },
  })

  const syncHealthData = useCallback(async () => {
    if (!isNative || !isConnected) return

    const now = Date.now()
    if (now - lastSyncRef.current < SYNC_DEBOUNCE_MS) return
    lastSyncRef.current = now

    const events = missingActivityEvents?.data?.data
    if (!events?.length) return

    for (const event of events) {
      try {
        const startDate = new Date(event.start_time)
        const endDate = new Date(event.end_time)

        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
          continue
        }

        const startDateISO = startDate.toISOString()
        const endDateISO = endDate.toISOString()

        const [calories_res, steps_res, distance_res] = await Promise.all([
          Health.readSamples({
            dataType: 'calories',
            startDate: startDateISO,
            endDate: endDateISO,
            limit: 0,
          }).catch(() => ({ samples: [] })),

          Health.readSamples({
            dataType: 'steps',
            startDate: startDateISO,
            endDate: endDateISO,
            limit: 0,
          }).catch(() => ({ samples: [] })),

          Health.readSamples({
            dataType: 'distance',
            startDate: startDateISO,
            endDate: endDateISO,
            limit: 0,
          }).catch(() => ({ samples: [] })),
        ])

        const calories_value =
          calories_res?.samples?.reduce(
            (total: number, sample: any) => total + (sample?.value || 0),
            0
          ) || 0
        const steps_value =
          steps_res?.samples?.reduce(
            (total: number, sample: any) => total + (sample?.value || 0),
            0
          ) || 0
        const distance_value =
          distance_res?.samples?.reduce(
            (total: number, sample: any) => total + (sample?.value || 0),
            0
          ) || 0

        if (calories_value > 0 || steps_value > 0 || distance_value > 0) {
          putActivityData.mutate({
            data: {
              calories: calories_value,
              steps: steps_value,
              distance: distance_value,
            },
            id: event.id,
          })
        }
      } catch (eventError) {
        console.error(`[Health] Failed to process event ${event.id}:`, eventError)
      }
    }
  }, [isNative, isConnected, missingActivityEvents?.data?.data])

  useEffect(() => {
    if (!isNative || !isConnected) return

    const listener = App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        missingActivityEvents.refetch()
      }
    })

    return () => {
      listener.then((l) => l.remove())
    }
  }, [isNative, isConnected])

  useEffect(() => {
    syncHealthData()
  }, [missingActivityEvents.data])

  return { syncHealthData, missingActivityEvents }
}
